// Client-side image resize before upload.
// Project instructions Section 12: 2048px max edge, JPEG q=85. Non-negotiable.
// Videos are passed through untouched (compression there is a future concern).
//
// 2026-05-30: prefer running the heavy work in a Web Worker so picking 6+
// photos on a low-end Android doesn't lock the UI for 5-10 s. Falls back
// to the inline canvas path if workers aren't available (older iOS in
// Capacitor, very old browsers).

const MAX_EDGE = 2048
const JPEG_QUALITY = 0.85

// Singleton worker. Created lazily on first compression so the bundle
// doesn't try to spin it up before it's needed (and so SSR doesn't crash).
let worker: Worker | null = null
let nextRequestId = 1
const pending = new Map<number, (out: File | null) => void>()

function getWorker(): Worker | null {
  if (worker) return worker
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return null
  try {
    worker = new Worker(new URL('./photoCompressWorker.ts', import.meta.url), { type: 'module' })
    worker.addEventListener('message', e => {
      const { id, file, error } = e.data as { id: number; file?: File; error?: string }
      const resolve = pending.get(id)
      if (!resolve) return
      pending.delete(id)
      if (error || !file) resolve(null)
      else resolve(file)
    })
    return worker
  } catch {
    return null
  }
}

async function compressInWorker(file: File): Promise<File | null> {
  const w = getWorker()
  if (!w) return null
  return new Promise<File | null>(resolve => {
    const id = nextRequestId++
    pending.set(id, resolve)
    w.postMessage({ id, file })
    // Safety net — never wait more than 30s for a single image.
    setTimeout(() => { if (pending.delete(id)) resolve(null) }, 30_000)
  })
}

export async function compressForUpload(file: File): Promise<File> {
  // Try the worker path first. If unavailable or it fails, fall through
  // to the inline implementation so compression never silently drops.
  const fromWorker = await compressInWorker(file).catch(() => null)
  if (fromWorker) return fromWorker
  return compressInline(file)
}

async function compressInline(file: File): Promise<File> {
  // Only compress images. Videos / other types pass through.
  if (!file.type.startsWith('image/')) return file
  // HEIC and friends — leave alone, canvas can't decode them in most browsers.
  if (file.type === 'image/heic' || file.type === 'image/heif') return file

  try {
    const bitmap = await createImageBitmap(file)
    const { width, height } = bitmap
    const longestEdge = Math.max(width, height)
    const scale = longestEdge > MAX_EDGE ? MAX_EDGE / longestEdge : 1
    const targetW = Math.round(width * scale)
    const targetH = Math.round(height * scale)

    // If the image is already small enough AND already a JPEG, pass through.
    if (scale === 1 && file.type === 'image/jpeg') {
      bitmap.close?.()
      return file
    }

    const canvas = typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(targetW, targetH)
      : Object.assign(document.createElement('canvas'), { width: targetW, height: targetH })

    const ctx = (canvas as any).getContext('2d') as CanvasRenderingContext2D
    ctx.drawImage(bitmap, 0, 0, targetW, targetH)
    bitmap.close?.()

    const blob: Blob = await new Promise((resolve, reject) => {
      if ('convertToBlob' in canvas) {
        ;(canvas as OffscreenCanvas)
          .convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY })
          .then(resolve, reject)
      } else {
        ;(canvas as HTMLCanvasElement).toBlob(
          b => (b ? resolve(b) : reject(new Error('Canvas toBlob returned null'))),
          'image/jpeg',
          JPEG_QUALITY,
        )
      }
    })

    const baseName = file.name.replace(/\.[^.]+$/, '')
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() })
  } catch (e) {
    // If anything fails (corrupt EXIF, unsupported format, OOM), upload original.
    console.warn('[photoCompress] falling back to original', e)
    return file
  }
}

export async function compressAll(files: File[]): Promise<File[]> {
  return Promise.all(files.map(compressForUpload))
}

// Supabase Storage free-tier rejects > 50 MB files. Most short site videos
// are fine; long ones aren't. We surface this BEFORE the upload attempt so
// the user isn't left wondering why their clip vanished mid-save.
const HARD_LIMIT_BYTES = 50 * 1024 * 1024
const WARN_LIMIT_BYTES = 30 * 1024 * 1024

export interface FileSizeCheck {
  accepted: File[]
  rejected: { name: string; sizeMb: number }[]
  warnings: { name: string; sizeMb: number }[]
}

/** Split a file list into accepted (<=50MB), rejected (>50MB), and warned
 *  (>30MB) buckets. Images skip the check — only video / large blobs hit it. */
export function checkUploadSizes(files: File[]): FileSizeCheck {
  const accepted: File[] = []
  const rejected: { name: string; sizeMb: number }[] = []
  const warnings: { name: string; sizeMb: number }[] = []
  for (const f of files) {
    const sizeMb = Math.round((f.size / (1024 * 1024)) * 10) / 10
    if (f.size > HARD_LIMIT_BYTES) {
      rejected.push({ name: f.name || 'video', sizeMb })
      continue
    }
    if (f.size > WARN_LIMIT_BYTES) warnings.push({ name: f.name || 'video', sizeMb })
    accepted.push(f)
  }
  return { accepted, rejected, warnings }
}

export function formatSizeMessage(c: FileSizeCheck): string | null {
  const parts: string[] = []
  if (c.rejected.length) {
    parts.push(
      `Skipped ${c.rejected.length} file${c.rejected.length > 1 ? 's' : ''} over 50 MB ` +
      `(${c.rejected.map(r => `${r.name} ${r.sizeMb}MB`).join(', ')}). ` +
      `Storage limit — film shorter clips or trim before uploading.`
    )
  }
  if (c.warnings.length) {
    parts.push(
      `Large file${c.warnings.length > 1 ? 's' : ''} (${c.warnings.map(w => `${w.sizeMb}MB`).join(', ')}) ` +
      `— upload may be slow on a phone signal.`
    )
  }
  return parts.length ? parts.join(' ') : null
}
